import { motion } from "framer-motion";
import Logo from "@/components/7fcc/Logo";
import CTAButton from "@/components/7fcc/CTAButton";
import { HOME } from "@/data/homeContent";
import { EASE } from "@/lib/motion";

// Homepage hero — same brand treatment as /ready, two CTAs.
export default function HomeHero() {
  const h = HOME.hero;
  return (
    <>
      <section
        className="relative flex flex-col items-center overflow-hidden bg-ink-950 px-6 pb-14 pt-12 text-center md:pb-16 md:pt-14"
        data-testid="home-hero"
      >
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-[2%] h-[42vh] w-[70vw] max-w-[880px] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(61,126,255,0.1),transparent_65%)] blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.96, filter: "blur(10px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative w-[min(420px,70vw)] mix-blend-screen"
        >
          <Logo halo priority className="w-full" />
        </motion.div>

        <div className="relative mt-5 flex max-w-5xl flex-col items-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-light"
            data-testid="home-eyebrow"
          >
            <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
            {HOME.brand.eyebrow}
            <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
          </motion.p>

          <h1 className="font-display text-[clamp(1.75rem,4vw,3.1rem)] font-semibold uppercase leading-[1.08] tracking-[-0.01em]">
            {h.headlineLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="text-chrome block"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.13, ease: EASE }}
                  data-testid={`home-headline-${i + 1}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-5 max-w-2xl text-base leading-relaxed text-fg-muted md:text-lg"
            data-testid="home-subhead"
          >
            {h.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 flex flex-wrap items-center justify-center gap-4"
          >
            <CTAButton
              label={h.ctaPrimary.label}
              targetId={h.ctaPrimary.anchor}
              testId="home-cta-assessment"
            />
            <CTAButton
              label={h.ctaSecondary.label}
              targetId={h.ctaSecondary.anchor}
              variant="chrome"
              testId="home-cta-method"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-9 max-w-2xl text-sm leading-relaxed text-fg-muted md:text-base"
            data-testid="home-trust-line"
          >
            {HOME.band.line1}
            <br />
            <span className="text-silver">{HOME.band.line2}</span>
          </motion.p>
        </div>

        {/* standards strip — inside the hero, as on the live page */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
          className="relative mt-10 w-full"
          data-testid="home-band"
        >
          <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-2.5">
            {HOME.band.chips.map((chip) => (
              <span
                key={chip}
                className="flex items-center gap-2 rounded-full border border-line bg-ink-950/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-silver-mid"
                data-testid={`home-chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="h-1 w-1 rounded-full bg-brand" aria-hidden="true" />
                {chip}
              </span>
            ))}
          </div>
        </motion.div>
      </section>
    </>
  );
}